import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const SESSION_KEY = 'league-intel.telemetry-session.v1';
const clean = value => String(value || '').replace(/[^a-zA-Z0-9_./: -]/g, '').slice(0, 120);

const sessionId = () => {
  let value = sessionStorage.getItem(SESSION_KEY);
  if (!value) {
    value = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SESSION_KEY, value);
  }
  return value;
};

export const telemetryTarget = node => {
  const element = node?.closest?.('button,a,[role="button"],input[type="submit"],input[type="button"]');
  if (!element) return '';
  if (element.dataset?.telemetry) return `data:${clean(element.dataset.telemetry)}`;
  if (element.id) return `id:${clean(element.id)}`;
  if (element.tagName === 'A' && element.getAttribute('href')) {
    try { return `link:${new URL(element.href, window.location.origin).pathname}`; } catch {}
  }
  const classes = [...(element.classList || [])].filter(name => /^(btn|nav|lor-|lia-|kwr-|hq-)/.test(name)).slice(0, 3);
  return `${String(element.tagName || 'control').toLowerCase()}${classes.length ? `.${classes.join('.')}` : ''}`;
};

const formTarget = form => {
  if (!form) return 'form';
  if (form.dataset?.telemetry) return `data:${clean(form.dataset.telemetry)}`;
  if (form.id) return `id:${clean(form.id)}`;
  const classes = [...(form.classList || [])].slice(0, 2);
  return `form${classes.length ? `.${classes.join('.')}` : ''}`;
};

export default function SiteTelemetry({ api, league, enabled = true }) {
  const location = useLocation();
  const queue = useRef([]);
  const forms = useRef(new Map());
  const recentClicks = useRef(new Map());
  const currentPage = useRef(location.pathname);

  const enqueue = useCallback((event_type, target = '', metadata = {}) => {
    if (!enabled) return;
    queue.current.push({ event_type, league, page: currentPage.current, target: clean(target), metadata });
    if (queue.current.length > 20) queue.current = queue.current.slice(-20);
  }, [enabled, league]);

  const flush = useCallback(() => {
    if (!enabled || !queue.current.length) return;
    const events = queue.current.splice(0, 20);
    fetch(`${api}/api/telemetry/events`, {
      method: 'POST', credentials: 'include', keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId(), events }),
    }).catch(() => {});
  }, [api, enabled]);

  useEffect(() => {
    for (const [target, state] of forms.current.entries()) {
      if (!state.submitted) enqueue('form_abandon', target);
    }
    forms.current.clear();
    currentPage.current = location.pathname;
    enqueue('page_view');
    const timer = setTimeout(flush, 250);
    return () => clearTimeout(timer);
  }, [location.pathname, enqueue, flush]);

  useEffect(() => {
    if (!enabled) return undefined;
    const interval = setInterval(flush, 4000);
    const click = event => {
      const target = telemetryTarget(event.target);
      if (!target) return;
      enqueue('click', target);
      const now = Date.now();
      const previous = recentClicks.current.get(target) || [];
      const windowed = [...previous.filter(value => now - value < 1800), now];
      recentClicks.current.set(target, windowed);
      if (windowed.length === 3) enqueue('rage_click', target);
    };
    const focus = event => {
      const form = event.target?.closest?.('form');
      if (!form) return;
      const target = formTarget(form);
      if (!forms.current.has(target)) {
        forms.current.set(target, { submitted: false });
        enqueue('form_start', target);
      }
    };
    const submit = event => {
      const target = formTarget(event.target);
      forms.current.set(target, { submitted: true });
      enqueue('form_submit', target);
      setTimeout(flush, 0);
    };
    const runtimeError = event => enqueue('client_error', '', {
      error_kind: event.target && event.target !== window ? 'resource' : 'runtime',
    });
    const rejection = () => enqueue('client_error', '', { error_kind: 'promise' });
    const custom = event => enqueue(event.detail?.event_type || 'click', event.detail?.target || '', event.detail?.metadata || {});
    document.addEventListener('click', click, true);
    document.addEventListener('focusin', focus, true);
    document.addEventListener('submit', submit, true);
    window.addEventListener('error', runtimeError, true);
    window.addEventListener('unhandledrejection', rejection);
    window.addEventListener('leagueintel:telemetry', custom);
    const perfTimer = setTimeout(() => {
      const nav = performance.getEntriesByType?.('navigation')?.[0];
      if (nav) enqueue('performance', '', {
        duration_ms: Math.round(nav.duration || 0), navigation_type: clean(nav.type),
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      });
      flush();
    }, 3000);
    const unload = () => {
      for (const [target, state] of forms.current.entries()) if (!state.submitted) enqueue('form_abandon', target);
      forms.current.clear();
      flush();
    };
    window.addEventListener('pagehide', unload);
    return () => {
      clearInterval(interval); clearTimeout(perfTimer); unload();
      document.removeEventListener('click', click, true);
      document.removeEventListener('focusin', focus, true);
      document.removeEventListener('submit', submit, true);
      window.removeEventListener('error', runtimeError, true);
      window.removeEventListener('unhandledrejection', rejection);
      window.removeEventListener('leagueintel:telemetry', custom);
      window.removeEventListener('pagehide', unload);
    };
  }, [enabled, enqueue, flush]);

  return null;
}
